import DOMPurify from "dompurify"

const TAG_STRIP_REGEX = /<[^>]*>/g
const ATTACHMENT_ATTR_REGEX = /data-attachment-id=/

/** An attachment chip carries no visible text of its own, so a stripped-tag
 * check alone would call a note with only an attachment "empty". */
const isRichTextEmpty = (html?: string | null): boolean => {
  if (!html) return true
  if (ATTACHMENT_ATTR_REGEX.test(html)) return false
  return !html.replace(TAG_STRIP_REGEX, "").replace(/&nbsp;/g, " ").trim()
}

const ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "ul",
  "ol",
  "li",
  "a",
  "span",
  "h2"
]

// No `class` here on purpose: it would let saved HTML (written by anyone with
// write access to internal_notes, not only through this editor's own
// toolbar) carry an arbitrary Tailwind utility class into every other
// viewer's page — e.g. a full-viewport overlay. Every look this editor
// produces is keyed off `href`/`data-attachment-id` in CSS instead (see
// rich-text-editor.css), so nothing here depends on `class` surviving.
const ALLOWED_ATTR = [
  "href",
  "target",
  "rel",
  "data-attachment-id",
  "data-attachment-name"
]

/** The one place saved note HTML is trusted for `dangerouslySetInnerHTML` —
 * every render path (view mode, both entities' cards) must go through this. */
const sanitizeRichTextHtml = (html?: string | null): string =>
  html
    ? DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR })
    : ""

// Matches only tags this editor itself ever emits — not `<[a-z][\s\S]*>` in
// general, which also matches plain text like "reach us at <sales@acme.com>"
// and would then run that text through the sanitizer as markup, silently
// dropping the bracketed part as an unrecognized tag.
const HTML_TAG_PROBE = /<(p|ul|ol|li|a|strong|b|em|i|u|span|h2|br)[\s/>]/i
const ESCAPE_MAP: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;"
}
const escapeHtml = (raw: string) =>
  raw.replace(/[&<>"']/g, (char) => ESCAPE_MAP[char])

/**
 * A note saved before this editor existed is a plain string with `\n`
 * breaks, not HTML — rendering it unconverted through `innerHTML` would
 * collapse every line onto one. Detected once, up front: real editor output
 * always opens with a recognizable tag, so a string with none is legacy.
 */
const toDisplayHtml = (raw: string): string =>
  HTML_TAG_PROBE.test(raw)
    ? raw
    : `<p>${escapeHtml(raw).replace(/\r?\n/g, "<br/>")}</p>`

const prepareRichTextForDisplay = (raw?: string | null): string =>
  sanitizeRichTextHtml(raw ? toDisplayHtml(raw) : "")

export {
  isRichTextEmpty,
  prepareRichTextForDisplay,
  sanitizeRichTextHtml,
  toDisplayHtml
}
